/**
 * CadencePanel — M-Rhythm-1 rewrite, Wave-3 rebuild
 *
 * Structure Settings → Cadence tab. Manages the full cadence schedule
 * configuration: active status, structured schedule_config, agenda templates,
 * default participants, and per-schedule defaults (accountable actor, meeting
 * moderator).
 *
 * Wave-3 fixes (C1-C9):
 *  C1  — Daily Check-in: weekday multi-select buttons restored.
 *  C2  — Weekly Operating Review: weekday dropdown restored.
 *  C3  — Monthly System Review: ordinal × weekday selectors restored.
 *  C4  — Quarterly/Trimester: single horizontal row (no-wrap).
 *  C5  — Quarterly: "period" word added after upcoming/current dropdown.
 *  C6  — Annual: single horizontal row (no-wrap).
 *  C7  — Defaults: Accountable + Moderator displayed side-by-side.
 *  C8  — Participants: expandable ParticipantPicker per cadence row.
 *  C9  — Agenda: estimated total duration shown in section header.
 *
 * Save flow (sequential, fail-fast):
 *  1. Planning-cycle change → PATCH /tenant-settings (if changed)
 *  2. Per-row dirty fields:
 *     a. is_active / schedule_expression → PUT /cadence/schedules/:id
 *     b. schedule_config → PUT /cadence/schedules/:id/config
 *     c. Agenda template → PUT /cadence/schedules/:id/agenda-template
 *     d. Defaults (accountable + moderator + participants) → PUT /cadence/schedules/:id/defaults
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useRorosCadenceSchedules,
  useRorosCadenceScheduleUpdate,
  useRorosCadenceScheduleConfigUpdate,
  useRorosCadenceAgendaTemplateSet,
  useRorosCadenceScheduleDefaults,
} from '@/hooks/roros/cadence';
import { useRorosTenantSettings, useRorosTenantSettingsUpdate } from '@/hooks/roros/launchpad';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';
import { PERMISSIONS } from '@/constants/permissions';
import { CADENCE_SCHEDULES } from '@/routes/roros/launchpad/types';
import { LoadingState } from '@/components/ui/LoadingState';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Switch } from '@/components/ui/Switch';
import { Select } from '@/components/ui/Select';
import { TimePicker } from '@/components/ui/TimePicker';
import { showToast } from '@/components/ui/Toast';
import { AccountablePicker } from '@/components/roros/AccountablePicker';
import { ParticipantPicker, type ParticipantEntry } from '@/components/roros/ParticipantPicker';
import { AgendaEditor, type AgendaItemDraft } from '@/components/roros/AgendaEditor';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosCadenceService, type CadenceScheduleConfig, type CadenceAgendaItemDTO } from '@/services/roros/cadence.service';
import type { RorosCadenceScheduleDTO } from '@/services/roros/cadence.service';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';
import {
  parseTimeFromCron,
  parseSchedule,
  serializeSchedule,
  defaultScheduleFor,
  type CadenceScheduleType,
  type ParsedSchedule,
} from '@/components/roros/cadence/scheduleExpression';

const ACTOR_DIRECTORY_PARAMS = { pageSize: '100', sortBy: 'name', sortOrder: 'asc' } as const;

function actorDirectoryParams(q: string): Record<string, string> {
  const trimmed = q.trim();
  return trimmed ? { ...ACTOR_DIRECTORY_PARAMS, q: trimmed } : { ...ACTOR_DIRECTORY_PARAMS };
}

function mergeActors(...sources: Array<RorosActorDTO[] | null | undefined>): RorosActorDTO[] {
  const byId = new Map<string, RorosActorDTO>();
  for (const source of sources) {
    for (const actor of source ?? []) {
      byId.set(actor.id, actor);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Weekday helpers ─────────────────────────────────────────────────────────

const WEEKDAY_STR_OPTIONS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
] as const;

/** Short labels for the Daily multi-toggle buttons */
const WEEKDAY_SHORT = [
  { value: 'mon', label: 'Mo' },
  { value: 'tue', label: 'Tu' },
  { value: 'wed', label: 'We' },
  { value: 'thu', label: 'Th' },
  { value: 'fri', label: 'Fr' },
  { value: 'sat', label: 'Sa' },
  { value: 'sun', label: 'Su' },
] as const;

const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const ORDINAL_OPTIONS = [
  { value: 'first', label: '1st' },
  { value: 'second', label: '2nd' },
  { value: 'third', label: '3rd' },
  { value: 'fourth', label: '4th' },
  { value: 'last', label: 'Last' },
];

const OFFSET_WEEKS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

// ─── Row state model ──────────────────────────────────────────────────────────

interface RowDraft {
  scheduleId: string;
  cadenceType: string;
  isActive: boolean;
  /** Used for is_active / schedule_expression save path (backward compat) */
  time: string;
  parsedSchedule: ParsedSchedule;
  scheduleExpression: string;
  /** M-Rhythm-1: structured config (new) */
  scheduleConfig: CadenceScheduleConfig;
  defaultAccountableActorId: string | null;
  defaultModeratorActorId: string | null;
  /** C8 — Wave 3: default participants for materialized meetings */
  defaultParticipants: ParticipantEntry[];
  /** null = not yet fetched; [] = loaded, empty */
  agendaItems: AgendaItemDraft[] | null;
  /** W8-1: pre-computed count from server DTO — shown in header before lazy-load */
  serverAgendaItemCount: number;
  /** W8-1: pre-computed total duration from server DTO — shown in header before lazy-load */
  serverAgendaTotalDuration: number;
}

function agendaDTOsTosDrafts(items: CadenceAgendaItemDTO[]): AgendaItemDraft[] {
  return [...items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item) => ({
      key: item.id,
      title: item.title,
      accountableActorId: item.accountableActorId,
      durationMinutes: item.durationMinutes,
    }));
}

function buildDefaultScheduleConfig(
  type: string,
  dto: RorosCadenceScheduleDTO | undefined,
): CadenceScheduleConfig {
  if (dto?.scheduleConfig) return dto.scheduleConfig as CadenceScheduleConfig;

  // Build a sensible default from the existing cron / meta
  switch (type) {
    case 'quarterly':
    case 'trimester':
      return {
        time: dto ? parseTimeFromCron(dto.scheduleExpression) : '14:00',
        offset_weeks: 1,
        offset_direction: 'before',
        offset_anchor: 'start',
        offset_period: 'upcoming',
        weekday: 'mon',
      };
    case 'annual':
      return {
        time: dto ? parseTimeFromCron(dto.scheduleExpression) : '10:00',
        month: 12,
        ordinal: 'first',
        weekday: 'mon',
      };
    case 'daily':
      return {
        time: dto ? parseTimeFromCron(dto.scheduleExpression) : '09:00',
        weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      };
    case 'weekly':
      return {
        time: dto ? parseTimeFromCron(dto.scheduleExpression) : '10:00',
        weekday: 'mon',
      };
    case 'monthly':
      return {
        time: dto ? parseTimeFromCron(dto.scheduleExpression) : '14:00',
        ordinal: 'first',
        weekday: 'mon',
      };
    default:
      return { time: '09:00' };
  }
}

/**
 * Parse default_participants from the DTO. The server returns this as a JSONB
 * array; convert to ParticipantEntry[] (adding client-side keys).
 */
function parseDefaultParticipants(dto: RorosCadenceScheduleDTO | undefined): ParticipantEntry[] {
  const raw = (dto as (RorosCadenceScheduleDTO & { defaultParticipants?: unknown }) | undefined)
    ?.defaultParticipants;
  if (!Array.isArray(raw)) return [];
  let counter = 0;
  return raw
    .filter(
      (p): p is { participantKind: string; actorId: string | null; domainId: string | null; role: string } =>
        !!p && typeof p === 'object',
    )
    .map((p) => ({
      key: `dp-init-${++counter}`,
      participantKind: (p.participantKind === 'domain_accountable' ? 'domain_accountable' : 'actor') as 'actor' | 'domain_accountable',
      actorId: p.actorId ?? null,
      domainId: p.domainId ?? null,
      role: (p.role === 'observer' ? 'observer' : 'attendee') as 'attendee' | 'observer',
      displayName: null,
    }));
}

function buildRowDraft(
  type: string,
  dto: RorosCadenceScheduleDTO | undefined,
): Omit<RowDraft, 'agendaItems'> {
  const meta = CADENCE_SCHEDULES.find((m) => m.type === type)!;
  const time = dto ? parseTimeFromCron(dto.scheduleExpression) : meta.defaultTime;
  const parsedSchedule = dto
    ? parseSchedule(type as CadenceScheduleType, dto.scheduleExpression)
    : defaultScheduleFor(type as CadenceScheduleType);
  const scheduleConfig = buildDefaultScheduleConfig(type, dto);

  return {
    scheduleId: dto?.id ?? '',
    cadenceType: type,
    isActive: dto ? dto.isActive : meta.defaultActive,
    time,
    parsedSchedule,
    scheduleExpression: dto?.scheduleExpression ?? '',
    scheduleConfig,
    defaultAccountableActorId: dto?.defaultAccountableActorId ?? null,
    defaultModeratorActorId: dto?.defaultModeratorActorId ?? null,
    defaultParticipants: parseDefaultParticipants(dto),
    // W8-1: pre-computed counts from server — used in Agenda header before lazy-load
    serverAgendaItemCount: dto?.agendaItemCount ?? 0,
    serverAgendaTotalDuration: dto?.agendaTotalDuration ?? 0,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Expandable section wrapper used for Agenda, Defaults, and Participants sections. */
function ExpandableSection({
  label,
  expanded,
  onToggle,
  children,
  testId,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="border-t border-border/50">
      <Button
        type="button"
        variant="ghost"
        className="w-full flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        onClick={onToggle}
        data-testid={testId}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        {label}
      </Button>
      {expanded && (
        <div className="px-4 pb-3 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── C1: Daily Check-in scheduler ────────────────────────────────────────────

/**
 * Daily scheduler: [Mo][Tu][We][Th][Fr][Sa][Su] · [time]
 * Multi-select weekday toggle buttons + time picker on one horizontal line.
 */
function DailyScheduler({
  config,
  onChange,
  disabled,
}: {
  config: CadenceScheduleConfig;
  onChange: (next: CadenceScheduleConfig) => void;
  disabled?: boolean;
}) {
  const patch = (partial: Partial<CadenceScheduleConfig>) =>
    onChange({ ...config, ...partial });

  const activeWeekdays = config.weekdays ?? ['mon', 'tue', 'wed', 'thu', 'fri'];

  const toggleWeekday = (day: string) => {
    const next = activeWeekdays.includes(day)
      ? activeWeekdays.filter((d) => d !== day)
      : [...activeWeekdays, day];
    // Always keep at least one day selected
    if (next.length > 0) patch({ weekdays: next });
  };

  return (
    <div className="flex flex-nowrap justify-start items-center gap-1.5 text-sm" data-testid="daily-scheduler">
      {/* Weekday multi-select buttons */}
      <div className="flex items-center gap-0.5 shrink-0" role="group" aria-label={t('Select weekdays')}>
        {WEEKDAY_SHORT.map((wd) => {
          const selected = activeWeekdays.includes(wd.value);
          return (
            <Button
              key={wd.value}
              type="button"
              variant={selected ? 'primary' : 'outline'}
              size="sm"
              className="h-7 w-8 p-0 text-xs font-medium"
              disabled={disabled}
              onClick={() => toggleWeekday(wd.value)}
              aria-pressed={selected}
              aria-label={t(wd.label)}
              data-testid={`daily-weekday-${wd.value}`}
            >
              {t(wd.label)}
            </Button>
          );
        })}
      </div>

      <span className="text-muted-foreground shrink-0">·</span>

      {/* Time picker */}
      <TimePicker
        value={config.time ?? '09:00'}
        onChange={(val) => patch({ time: val ?? '09:00' })}
        use24Hour={true}
        minuteStep={15}
        disabled={disabled}
        className="w-32 shrink-0"
      />
    </div>
  );
}

// ─── C2: Weekly Operating Review scheduler ───────────────────────────────────

/** Weekly scheduler: [Weekday] · [time] */
function WeeklyScheduler({
  config,
  onChange,
  disabled,
}: {
  config: CadenceScheduleConfig;
  onChange: (next: CadenceScheduleConfig) => void;
  disabled?: boolean;
}) {
  const patch = (partial: Partial<CadenceScheduleConfig>) =>
    onChange({ ...config, ...partial });

  return (
    <div className="flex flex-nowrap justify-start items-center gap-1.5 text-sm" data-testid="weekly-scheduler">
      <Select
        value={config.weekday ?? 'mon'}
        onValueChange={(v) => patch({ weekday: v })}
        options={WEEKDAY_STR_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-32 shrink-0"
        data-testid="weekly-weekday"
      />

      <span className="text-muted-foreground shrink-0">·</span>

      <TimePicker
        value={config.time ?? '10:00'}
        onChange={(val) => patch({ time: val ?? '10:00' })}
        use24Hour={true}
        minuteStep={15}
        disabled={disabled}
        className="w-32 shrink-0"
      />
    </div>
  );
}

// ─── C3: Monthly System Review scheduler ─────────────────────────────────────

/** Monthly scheduler: [1st/2nd/...] · [Weekday] · [time] */
function MonthlyScheduler({
  config,
  onChange,
  disabled,
}: {
  config: CadenceScheduleConfig;
  onChange: (next: CadenceScheduleConfig) => void;
  disabled?: boolean;
}) {
  const patch = (partial: Partial<CadenceScheduleConfig>) =>
    onChange({ ...config, ...partial });

  return (
    <div className="flex flex-nowrap justify-start items-center gap-1.5 text-sm" data-testid="monthly-scheduler">
      {/* Ordinal */}
      <Select
        value={config.ordinal ?? 'first'}
        onValueChange={(v) => patch({ ordinal: v })}
        options={ORDINAL_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-24 shrink-0"
        data-testid="monthly-ordinal"
      />

      {/* Weekday */}
      <Select
        value={config.weekday ?? 'mon'}
        onValueChange={(v) => patch({ weekday: v })}
        options={WEEKDAY_STR_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-32 shrink-0"
        data-testid="monthly-weekday"
      />

      <span className="text-muted-foreground shrink-0">·</span>

      <TimePicker
        value={config.time ?? '14:00'}
        onChange={(val) => patch({ time: val ?? '14:00' })}
        use24Hour={true}
        minuteStep={15}
        disabled={disabled}
        className="w-32 shrink-0"
      />
    </div>
  );
}

// ─── C4+C5: Quarterly / Trimester offset-based scheduler ─────────────────────

/**
 * Quarterly/Trimester scheduler (C4 fix: horizontal no-wrap, C5 fix: "period" word):
 * [N weeks] [Before|After] [Start|End] of [upcoming|current] period · [weekday] · [time]
 */
function PeriodOffsetScheduler({
  config,
  onChange,
  disabled,
  cadenceType,
}: {
  config: CadenceScheduleConfig;
  onChange: (next: CadenceScheduleConfig) => void;
  disabled?: boolean;
  cadenceType: 'quarterly' | 'trimester';
}) {
  const patch = (partial: Partial<CadenceScheduleConfig>) =>
    onChange({ ...config, ...partial });

  return (
    // W8-2 (revised): keep flex-nowrap for horizontal layout (specced in Wave 3).
    // Original W8-2 fix (replacing flex-nowrap with flex-wrap) restored TimePicker
    // popover visibility but BROKE the horizontal one-row layout — fields stacked
    // vertically. Correct fix: remove overflow-x-auto (which was the actual clip
    // context) and KEEP flex-nowrap. The TimePicker popover renders via a portal
    // and is no longer clipped without overflow constraints.
    // W9-1: justify-start clusters controls on the left; TimePicker gets w-32 to
    // prevent it from stretching to fill remaining row space.
    <div className="flex flex-nowrap justify-start items-center gap-1.5 text-sm" data-testid="period-offset-scheduler">
      {/* N weeks */}
      <Select
        value={String(config.offset_weeks ?? 1)}
        onValueChange={(v) => patch({ offset_weeks: Number(v) })}
        options={OFFSET_WEEKS_OPTIONS}
        disabled={disabled}
        wrapperClassName="w-16 shrink-0"
        data-testid="period-offset-weeks"
      />
      <span className="text-muted-foreground shrink-0">{t('weeks')}</span>

      {/* Before / After */}
      <Select
        value={config.offset_direction ?? 'before'}
        onValueChange={(v) => patch({ offset_direction: v as 'before' | 'after' })}
        options={[
          { value: 'before', label: t('Before') },
          { value: 'after', label: t('After') },
        ]}
        disabled={disabled}
        wrapperClassName="w-24 shrink-0"
        data-testid="period-offset-direction"
      />

      {/* Start / End */}
      <Select
        value={config.offset_anchor ?? 'start'}
        onValueChange={(v) => patch({ offset_anchor: v as 'start' | 'end' })}
        options={[
          { value: 'start', label: t('Start') },
          { value: 'end', label: t('End') },
        ]}
        disabled={disabled}
        wrapperClassName="w-20 shrink-0"
        data-testid="period-offset-anchor"
      />

      <span className="text-muted-foreground shrink-0">{t('of')}</span>

      {/* Upcoming / Current */}
      <Select
        value={config.offset_period ?? 'upcoming'}
        onValueChange={(v) => patch({ offset_period: v as 'upcoming' | 'current' })}
        options={[
          { value: 'upcoming', label: t('upcoming') },
          { value: 'current', label: t('current') },
        ]}
        disabled={disabled}
        wrapperClassName="w-28 shrink-0"
        data-testid="period-offset-period"
      />

      {/* C5: "trimester" or "quarter" word after upcoming/current */}
      <span className="text-muted-foreground shrink-0">
        {cadenceType === 'trimester' ? t('trimester') : t('quarter')}
      </span>

      <span className="text-muted-foreground shrink-0">·</span>

      {/* Weekday */}
      <Select
        value={config.weekday ?? 'mon'}
        onValueChange={(v) => patch({ weekday: v })}
        options={WEEKDAY_STR_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-32 shrink-0"
        data-testid="period-weekday"
      />

      <span className="text-muted-foreground shrink-0">·</span>

      {/* Time */}
      <TimePicker
        value={config.time ?? '14:00'}
        onChange={(val) => patch({ time: val ?? '14:00' })}
        use24Hour={true}
        minuteStep={15}
        disabled={disabled}
        className="w-32 shrink-0"
      />
    </div>
  );
}

// ─── C6: Annual scheduler ─────────────────────────────────────────────────────

/**
 * Annual scheduler (C6 fix: horizontal no-wrap):
 * [Month] · [1st|2nd|...|Last] · [weekday] · [time]
 */
function AnnualScheduler({
  config,
  onChange,
  disabled,
}: {
  config: CadenceScheduleConfig;
  onChange: (next: CadenceScheduleConfig) => void;
  disabled?: boolean;
}) {
  const patch = (partial: Partial<CadenceScheduleConfig>) =>
    onChange({ ...config, ...partial });

  return (
    // W8-2 (revised): keep flex-nowrap for horizontal layout (same fix pattern
    // as PeriodOffsetScheduler — see comment there).
    // W9-1: justify-start + TimePicker w-32 to prevent stretching across full row.
    <div className="flex flex-nowrap justify-start items-center gap-1.5 text-sm" data-testid="annual-scheduler">
      {/* Month */}
      <Select
        value={String(config.month ?? 12)}
        onValueChange={(v) => patch({ month: Number(v) })}
        options={MONTH_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-32 shrink-0"
        data-testid="annual-month"
      />
      <span className="text-muted-foreground shrink-0">·</span>

      {/* Ordinal */}
      <Select
        value={config.ordinal ?? 'first'}
        onValueChange={(v) => patch({ ordinal: v })}
        options={ORDINAL_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-24 shrink-0"
        data-testid="annual-ordinal"
      />

      {/* Weekday */}
      <Select
        value={config.weekday ?? 'mon'}
        onValueChange={(v) => patch({ weekday: v })}
        options={WEEKDAY_STR_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
        disabled={disabled}
        wrapperClassName="w-32 shrink-0"
        data-testid="annual-weekday"
      />
      <span className="text-muted-foreground shrink-0">·</span>

      {/* Time */}
      <TimePicker
        value={config.time ?? '10:00'}
        onChange={(val) => patch({ time: val ?? '10:00' })}
        use24Hour={true}
        minuteStep={15}
        disabled={disabled}
        className="w-32 shrink-0"
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CadencePanel() {
  const schedulesQuery = useRorosCadenceSchedules();
  const tenantSettingsQuery = useRorosTenantSettings();
  const [activeActorSearchKey, setActiveActorSearchKey] = useState<string | null>(null);
  const [actorSearch, setActorSearch] = useState('');
  const debouncedActorSearch = useDebounce(actorSearch.trim(), 250);
  // F-MUT1-P9: base server-max page plus server-side actor search for >100-actor orgs.
  const baseActorsQuery = useRorosActors(actorDirectoryParams(''));
  const searchedActorsQuery = useRorosActors(actorDirectoryParams(debouncedActorSearch));
  const domainsQuery = useRorosDomains();
  const updateSchedule = useRorosCadenceScheduleUpdate();
  const updateScheduleConfig = useRorosCadenceScheduleConfigUpdate();
  const setAgendaTemplate = useRorosCadenceAgendaTemplateSet();
  const setDefaults = useRorosCadenceScheduleDefaults();
  const updateTenantSettings = useRorosTenantSettingsUpdate();

  // TB-4 (AC-SSV-04): read-only mode for non-managers.
  const { hasPermission, isLoading: permsLoading } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_CADENCE_MANAGE);

  const planningCycle = useMemo<'quarterly' | 'trimester'>(() => {
    const raw = (tenantSettingsQuery.data as { planning_cycle?: string } | undefined)?.planning_cycle;
    return raw === 'trimester' ? 'trimester' : 'quarterly';
  }, [tenantSettingsQuery.data]);

  const operatingMode = useMemo<string>(() => {
    const raw = (tenantSettingsQuery.data as { operating_mode?: string } | undefined)?.operating_mode;
    return raw || 'founder';
  }, [tenantSettingsQuery.data]);

  const actors = useMemo<RorosActorDTO[]>(() => mergeActors(
    (baseActorsQuery.data ?? []) as RorosActorDTO[],
    (searchedActorsQuery.data ?? []) as RorosActorDTO[],
  ), [baseActorsQuery.data, searchedActorsQuery.data]);

  const domains = useMemo<RorosDomainDTO[]>(() => {
    const raw = domainsQuery.data as RorosDomainDTO[] | undefined;
    return raw ?? [];
  }, [domainsQuery.data]);

  // ─── Local state ───────────────────────────────────────────────────────────

  const [editedCycle, setEditedCycle] = useState<'quarterly' | 'trimester'>('quarterly');
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [expandedAgenda, setExpandedAgenda] = useState<Set<string>>(new Set());
  // AC-MUT2-3: expandedDefaults removed — Defaults section removed (Accountable moved to timing row)
  const [expandedParticipants, setExpandedParticipants] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Track server snapshot for dirty detection
  const serverSnapshotRef = useRef<{ rows: RowDraft[]; cycle: 'quarterly' | 'trimester' } | null>(null);

  // Initialise from server data
  const dtos = schedulesQuery.data as RorosCadenceScheduleDTO[] | undefined;
  useEffect(() => {
    if (!dtos) return;
    const byType = new Map(dtos.map((d) => [d.cadenceType, d]));
    // Wave-2 fix: preserve any already-loaded agenda items so that a schedules
    // refetch (triggered by onSuccess in setDefaults / updateScheduleConfig)
    // does not reset agendaItems to null and show "Agenda (0)" to the user.
    setRows((prevRows) => {
      const prevByType = new Map(prevRows.map((r) => [r.cadenceType, r]));
      const initialRows: RowDraft[] = CADENCE_SCHEDULES.map((meta) => {
        const prev = prevByType.get(meta.type);
        return {
          ...buildRowDraft(meta.type, byType.get(meta.type)),
          // Keep already-loaded agenda items; only reset on first mount (null)
          agendaItems: prev?.agendaItems ?? null,
        };
      });
      serverSnapshotRef.current = { rows: initialRows, cycle: planningCycle };
      return initialRows;
    });
    setEditedCycle(planningCycle);
  }, [dtos, planningCycle]);

  // ─── Agenda lazy-load ──────────────────────────────────────────────────────

  const loadAgendaForRow = useCallback(
    async (scheduleId: string) => {
      if (!scheduleId) return;
      setRows((prev) =>
        prev.map((r) => {
          if (r.scheduleId !== scheduleId || r.agendaItems !== null) return r;
          // Mark as "loading" with empty array to prevent duplicate fetches
          return { ...r, agendaItems: [] };
        }),
      );
      try {
        const items = await RorosCadenceService.listAgendaTemplate(scheduleId);
        setRows((prev) =>
          prev.map((r) =>
            r.scheduleId === scheduleId
              ? { ...r, agendaItems: agendaDTOsTosDrafts(items) }
              : r,
          ),
        );
      } catch {
        // Leave as [] on error — user can still add items
      }
    },
    [],
  );

  const toggleAgenda = useCallback(
    (scheduleId: string) => {
      setExpandedAgenda((prev) => {
        const next = new Set(prev);
        if (next.has(scheduleId)) {
          next.delete(scheduleId);
        } else {
          next.add(scheduleId);
          // Trigger lazy load if not loaded yet
          setRows((rows) => {
            const row = rows.find((r) => r.scheduleId === scheduleId);
            if (row && row.agendaItems === null) {
              loadAgendaForRow(scheduleId);
            }
            return rows;
          });
        }
        return next;
      });
    },
    [loadAgendaForRow],
  );

  // AC-MUT2-3: toggleDefaults removed — Defaults section removed (Accountable moved to timing row)

  const toggleParticipants = useCallback((scheduleId: string) => {
    setExpandedParticipants((prev) => {
      const next = new Set(prev);
      if (next.has(scheduleId)) next.delete(scheduleId);
      else next.add(scheduleId);
      return next;
    });
  }, []);

  // ─── Row updaters ──────────────────────────────────────────────────────────

  const updateRow = useCallback(<K extends keyof RowDraft>(
    scheduleId: string,
    field: K,
    value: RowDraft[K],
  ) => {
    setRows((prev) =>
      prev.map((r) => (r.scheduleId === scheduleId ? { ...r, [field]: value } : r)),
    );
  }, []);

  const handleActiveToggle = useCallback(
    (scheduleId: string, newActive: boolean) => {
      updateRow(scheduleId, 'isActive', newActive);
    },
    [updateRow],
  );

  const handleScheduleConfigChange = useCallback(
    (scheduleId: string, config: CadenceScheduleConfig) => {
      updateRow(scheduleId, 'scheduleConfig', config);
    },
    [updateRow],
  );

  const handleAgendaChange = useCallback(
    (scheduleId: string, items: AgendaItemDraft[]) => {
      setRows((prev) =>
        prev.map((r) => (r.scheduleId === scheduleId ? { ...r, agendaItems: items } : r)),
      );
    },
    [],
  );

  const handleDefaultAccountableChange = useCallback(
    (scheduleId: string, actorId: string | null) => {
      updateRow(scheduleId, 'defaultAccountableActorId', actorId);
    },
    [updateRow],
  );

  const handleDefaultModeratorChange = useCallback(
    (scheduleId: string, actorId: string | null) => {
      updateRow(scheduleId, 'defaultModeratorActorId', actorId);
    },
    [updateRow],
  );

  const handleParticipantsChange = useCallback(
    (scheduleId: string, participants: ParticipantEntry[]) => {
      updateRow(scheduleId, 'defaultParticipants', participants);
    },
    [updateRow],
  );

  const handlePlanningCycleToggle = useCallback(() => {
    const newCycle = editedCycle === 'quarterly' ? 'trimester' : 'quarterly';
    setEditedCycle(newCycle);
    // Flip active toggles for Q/T rows
    setRows((prev) =>
      prev.map((r) => {
        if (r.cadenceType === 'quarterly') return { ...r, isActive: newCycle === 'quarterly' };
        if (r.cadenceType === 'trimester') return { ...r, isActive: newCycle === 'trimester' };
        return r;
      }),
    );
  }, [editedCycle]);

  // ─── Dirty detection ───────────────────────────────────────────────────────

  const isDirty = useMemo(() => {
    const snapshot = serverSnapshotRef.current;
    if (!snapshot) return false;
    if (snapshot.cycle !== editedCycle) return true;
    for (const row of rows) {
      const srv = snapshot.rows.find((r) => r.scheduleId === row.scheduleId);
      if (!srv) continue;
      if (row.isActive !== srv.isActive) return true;
      if (JSON.stringify(row.scheduleConfig) !== JSON.stringify(srv.scheduleConfig)) return true;
      if (row.defaultAccountableActorId !== srv.defaultAccountableActorId) return true;
      if (row.defaultModeratorActorId !== srv.defaultModeratorActorId) return true;
      if (JSON.stringify(row.defaultParticipants.map(({ key: _k, ...rest }) => rest)) !==
          JSON.stringify(srv.defaultParticipants.map(({ key: _k, ...rest }) => rest))) return true;
      if (row.agendaItems !== null && srv.agendaItems !== null) {
        if (JSON.stringify(row.agendaItems) !== JSON.stringify(srv.agendaItems)) return true;
      } else if (row.agendaItems !== null && row.agendaItems.length > 0) {
        return true;
      }
    }
    return false;
  }, [rows, editedCycle]);

  // ─── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const snapshot = serverSnapshotRef.current;
    if (!snapshot) return;

    setIsSaving(true);
    let savedCount = 0;
    try {
      // 1. Planning cycle (first — backend enforces Q/T mutual exclusion)
      if (snapshot.cycle !== editedCycle) {
        await updateTenantSettings.mutateAsync({ planningCycle: editedCycle });
        savedCount++;
      }

      // 2. Per-row changes
      for (const row of rows) {
        if (!row.scheduleId) continue;
        const srv = snapshot.rows.find((r) => r.scheduleId === row.scheduleId);
        if (!srv) continue;

        // 2a. is_active / schedule_expression
        const activeChanged = row.isActive !== srv.isActive;
        if (activeChanged) {
          await updateSchedule.mutateAsync({
            id: row.scheduleId,
            payload: { is_active: row.isActive },
          });
          savedCount++;
        }

        // 2b. schedule_config (for Q/T/Annual specifically; also daily/weekly/monthly when changed)
        const configChanged =
          JSON.stringify(row.scheduleConfig) !== JSON.stringify(srv.scheduleConfig);
        if (configChanged) {
          await updateScheduleConfig.mutateAsync({
            id: row.scheduleId,
            config: row.scheduleConfig,
          });
          savedCount++;
        }

        // 2c. Agenda template (only if we loaded + edited it)
        if (row.agendaItems !== null) {
          const srvAgenda = srv.agendaItems ?? [];
          if (JSON.stringify(row.agendaItems) !== JSON.stringify(srvAgenda)) {
            await setAgendaTemplate.mutateAsync({
              id: row.scheduleId,
              items: row.agendaItems.map((item, idx) => ({
                title: item.title,
                accountable_actor_id: item.accountableActorId,
                duration_minutes: item.durationMinutes,
                sort_order: idx,
              })),
            });
            savedCount++;
          }
        }

        // 2d. Defaults (accountable + moderator + participants)
        const defaultsChanged =
          row.defaultAccountableActorId !== srv.defaultAccountableActorId ||
          row.defaultModeratorActorId !== srv.defaultModeratorActorId ||
          JSON.stringify(row.defaultParticipants.map(({ key: _k, ...rest }) => rest)) !==
            JSON.stringify(srv.defaultParticipants.map(({ key: _k, ...rest }) => rest));
        if (defaultsChanged) {
          await setDefaults.mutateAsync({
            id: row.scheduleId,
            payload: {
              default_accountable_actor_id: row.defaultAccountableActorId,
              default_moderator_actor_id: row.defaultModeratorActorId,
              // C8: persist default participants as JSONB array (key stripped — not stored)
              default_participants: row.defaultParticipants.map(({ key: _k, displayName: _d, ...rest }) => rest),
            },
          });
          savedCount++;
        }
      }

      // Refresh snapshot
      serverSnapshotRef.current = { rows: rows.map((r) => ({ ...r })), cycle: editedCycle };

      showToast.success(
        savedCount === 1
          ? t('Cadence updated — 1 change saved.')
          : t('Cadence updated — {n} changes saved.').replace('{n}', String(savedCount)),
      );
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to save cadence changes'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    const snapshot = serverSnapshotRef.current;
    if (!snapshot) return;
    setRows(snapshot.rows.map((r) => ({ ...r })));
    setEditedCycle(snapshot.cycle);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (schedulesQuery.isLoading || tenantSettingsQuery.isLoading) {
    return <LoadingState />;
  }
  if (schedulesQuery.isError) {
    return (
      <Alert variant="error">
        {t('Failed to load cadence schedules.')}{' '}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 underline"
          onClick={() => schedulesQuery.refetch()}
        >
          {t('Retry')}
        </Button>
      </Alert>
    );
  }
  if (rows.length === 0) {
    return (
      <Alert variant="info">
        {t('No cadence schedules found for this organisation yet. Re-run the Launchpad wizard to seed them.')}
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="cadence-panel">
      {/* TB-4 (AC-SSV-04): Read-only hint for non-managers. */}
      {!canManage && !permsLoading && (
        <Alert variant="info" title={t('Read-only view')} data-testid="cadence-readonly-hint">
          {t('You can view the cadence configuration, but you need the Cadence Manage permission to edit schedules or save changes.')}
        </Alert>
      )}

      {/* Cadence rows */}
      <div className="border border-border rounded-lg overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_auto] bg-muted border-b border-border">
          <div className="px-4 py-3 text-sm font-medium text-muted-foreground">{t('Cadence')}</div>
          <div className="px-4 py-3 text-sm font-medium text-muted-foreground text-center">{t('Active')}</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-border">
          {rows.map((row) => {
            const meta = CADENCE_SCHEDULES.find((m) => m.type === row.cadenceType);
            if (!meta) return null;

            // Hide Q or T based on current planning cycle selection
            const isHiddenByPlanningCycle =
              (row.cadenceType === 'quarterly' && editedCycle === 'trimester') ||
              (row.cadenceType === 'trimester' && editedCycle === 'quarterly');
            if (isHiddenByPlanningCycle) return null;

            const isRecommended = meta.recommendedFor.includes(operatingMode);
            const isQT =
              row.cadenceType === 'quarterly' || row.cadenceType === 'trimester';
            const isAnnual = row.cadenceType === 'annual';
            const isDaily = row.cadenceType === 'daily';
            const isWeekly = row.cadenceType === 'weekly';
            const isMonthly = row.cadenceType === 'monthly';

            const agendaExpanded = expandedAgenda.has(row.scheduleId);
            // AC-MUT2-3: defaultsExpanded removed — Defaults section removed
            const participantsExpanded = expandedParticipants.has(row.scheduleId);
            const actorSearchId = row.scheduleId || row.cadenceType;
            const accountableSearchKey = `cadence-accountable-${actorSearchId}`;
            const participantsSearchKey = `cadence-participants-${actorSearchId}`;

            // C9 + W8-1: compute agenda duration sum for the section label.
            // When agendaItems is null (not yet lazy-loaded), fall back to the
            // server-precomputed counts so the collapsed header shows correctly
            // from the very first render without requiring an expand.
            const agendaItems = row.agendaItems;
            let agendaCount: number;
            let agendaTotalMinutes: number;
            if (agendaItems === null) {
              // Not yet loaded — use server-precomputed counts (W8-1 fix)
              agendaCount = row.serverAgendaItemCount;
              agendaTotalMinutes = row.serverAgendaTotalDuration;
            } else {
              agendaCount = agendaItems.length;
              const agendaWithDuration = agendaItems.filter((i) => i.durationMinutes != null && i.durationMinutes > 0);
              agendaTotalMinutes = agendaWithDuration.reduce(
                (sum, i) => sum + (i.durationMinutes ?? 0),
                0,
              );
            }

            // Build agenda section label (C9)
            let agendaLabel = agendaCount > 0 ? `${t('Agenda')} (${agendaCount})` : t('Agenda');
            if (agendaTotalMinutes > 0) {
              agendaLabel += ` · ${t('Est.')} ${agendaTotalMinutes} ${t('min')}`;
            }

            const participantCount = row.defaultParticipants.length;

            return (
              <div
                key={row.cadenceType}
                className={(row.isActive || meta.alwaysActive) ? 'bg-card' : 'bg-muted/30'}
                data-cadence-row={row.cadenceType}
              >
                {/* Main row */}
                <div className="grid grid-cols-[1fr_auto] items-start gap-3 px-4 py-3">
                  {/* Cadence info + schedule pickers */}
                  <div className="min-w-0 space-y-2">
                    {/* Label row */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground text-sm">{t(meta.label)}</span>
                      {isRecommended && (
                        <Badge variant="success" className="text-[10px] px-1.5">{t('Recommended')}</Badge>
                      )}
                      {/* AC-MUT1-11: disabled types get a clear off-badge + calendar hint */}
                      {!row.isActive && !meta.alwaysActive && (
                        <Badge variant="secondary" className="text-[10px] px-1.5" data-testid={`cadence-off-badge-${row.cadenceType}`}>
                          {t('Off')}
                        </Badge>
                      )}
                      {/* Planning Cycle toggle — inline in the Q/T row (hidden for non-managers) */}
                      {isQT && canManage && (
                        <div className="flex items-center gap-2 ml-2 bg-muted border border-border rounded-md px-2 py-0.5">
                          <span
                            className={`text-xs font-medium ${
                              editedCycle === 'quarterly' ? 'text-primary-600' : 'text-muted-foreground'
                            }`}
                          >
                            {t('Quarterly')}
                          </span>
                          <Switch
                            checked={editedCycle === 'trimester'}
                            onChange={handlePlanningCycleToggle}
                            data-testid="planning-cycle-toggle"
                          />
                          <span
                            className={`text-xs font-medium ${
                              editedCycle === 'trimester' ? 'text-primary-600' : 'text-muted-foreground'
                            }`}
                          >
                            {t('Trimester')}
                          </span>
                        </div>
                      )}
                      {/* TB-4 (AC-SSV-04): Non-managers see planning cycle label as read-only text. */}
                      {isQT && !canManage && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {editedCycle === 'trimester' ? t('Trimester') : t('Quarterly')}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">{t(meta.description)}</p>
                    {/* AC-MUT1-11: hint for disabled types */}
                    {!row.isActive && !meta.alwaysActive && (
                      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid={`cadence-disabled-hint-${row.cadenceType}`}>
                        {t('Switched off — will not appear in the calendar')}
                      </p>
                    )}

                    {/* Schedule pickers — ALL types render inline in column 1
                        AC-MUT2-3: Accountable picker appended right of scheduler (managers only) */}
                    {row.scheduleId && (
                      <div className="mt-1 flex flex-wrap items-start gap-x-4 gap-y-1.5">
                        <div>
                          {/* C1: Daily — weekday multi-select + time */}
                          {isDaily && (
                            <DailyScheduler
                              config={row.scheduleConfig}
                              onChange={(c) => handleScheduleConfigChange(row.scheduleId, c)}
                              disabled={!row.isActive || !canManage}
                            />
                          )}
                          {/* C2: Weekly — weekday select + time */}
                          {isWeekly && (
                            <WeeklyScheduler
                              config={row.scheduleConfig}
                              onChange={(c) => handleScheduleConfigChange(row.scheduleId, c)}
                              disabled={!row.isActive || !canManage}
                            />
                          )}
                          {/* C3: Monthly — ordinal × weekday + time */}
                          {isMonthly && (
                            <MonthlyScheduler
                              config={row.scheduleConfig}
                              onChange={(c) => handleScheduleConfigChange(row.scheduleId, c)}
                              disabled={!row.isActive || !canManage}
                            />
                          )}
                          {/* C4+C5: Quarterly/Trimester — horizontal one-row with "quarter"/"trimester" word */}
                          {isQT && (
                            <PeriodOffsetScheduler
                              config={row.scheduleConfig}
                              onChange={(c) => handleScheduleConfigChange(row.scheduleId, c)}
                              disabled={!row.isActive || !canManage}
                              cadenceType={row.cadenceType as 'quarterly' | 'trimester'}
                            />
                          )}
                          {/* C6: Annual — horizontal one-row */}
                          {isAnnual && (
                            <AnnualScheduler
                              config={row.scheduleConfig}
                              onChange={(c) => handleScheduleConfigChange(row.scheduleId, c)}
                              disabled={!canManage}
                            />
                          )}
                        </div>
                        {/* AC-MUT2-3: Accountable in timing row — managers only */}
                        {canManage && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {t('Accountable')}
                            </span>
                            <AccountablePicker
                              value={{ actorId: row.defaultAccountableActorId, domainId: null }}
                              onChange={({ actorId }) =>
                                handleDefaultAccountableChange(row.scheduleId, actorId)
                              }
                              actors={actors}
                              mode="actor-only"
                              disabled={isSaving}
                              actorSearchValue={activeActorSearchKey === accountableSearchKey ? actorSearch : ''}
                              onActorSearchChange={(value) => {
                                setActiveActorSearchKey(accountableSearchKey);
                                setActorSearch(value);
                              }}
                              actorSearchLoading={activeActorSearchKey === accountableSearchKey && searchedActorsQuery.isFetching && !!actorSearch.trim()}
                              placeholder={t('None')}
                              data-testid={`cadence-default-accountable-${row.cadenceType}`}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Active toggle — all rows (disabled for non-managers) */}
                  <div className="flex items-center justify-center pt-1">
                    {meta.alwaysActive ? (
                      <Badge variant="secondary" className="text-xs">{t('Always')}</Badge>
                    ) : (
                      <Switch
                        checked={row.isActive}
                        onChange={canManage ? () => handleActiveToggle(row.scheduleId, !row.isActive) : undefined}
                        disabled={!canManage}
                        data-testid={`cadence-toggle-${row.cadenceType}`}
                      />
                    )}
                  </div>
                </div>

                {/* AC-MUT2-4: Agenda + Participants side by side ≥md (stacked mobile).
                    AC-MUT2-3: Defaults section removed — Accountable moved to timing row above. */}
                {row.scheduleId && (
                  <div className={
                    canManage
                      ? 'grid md:grid-cols-2 divide-y divide-border md:divide-y-0 md:divide-x'
                      : ''
                  }>
                    {/* Expandable: Agenda (AC-SSV-07: visible to all members; editable only by managers) */}
                    <ExpandableSection
                      label={agendaLabel}
                      expanded={agendaExpanded}
                      onToggle={() => toggleAgenda(row.scheduleId)}
                      testId={`cadence-agenda-toggle-${row.cadenceType}`}
                    >
                      {row.agendaItems === null ? (
                        <p className="text-xs text-muted-foreground italic">{t('Loading…')}</p>
                      ) : (
                        <>
                          {/* AC-SSV-07: read-only hint for members */}
                          {!canManage && (
                            <p className="text-xs text-muted-foreground italic mb-2" data-testid={`cadence-agenda-readonly-hint-${row.cadenceType}`}>
                              {t('Agenda is view-only. Contact a manager to make changes.')}
                            </p>
                          )}
                          <AgendaEditor
                            items={row.agendaItems}
                            onChange={(items) => handleAgendaChange(row.scheduleId, items)}
                            actors={actors}
                            mode="template"
                            disabled={isSaving || !canManage}
                            data-testid={`cadence-agenda-editor-${row.cadenceType}`}
                          />
                          {/* C9: Show estimated total when there are items with durations */}
                          {agendaTotalMinutes > 0 && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {t('Estimated total:')} <span className="font-medium text-foreground">{agendaTotalMinutes} {t('min')}</span>
                            </p>
                          )}
                        </>
                      )}
                    </ExpandableSection>

                    {/* Expandable: Participants (C8 / AC-MUT2-4: right column ≥md) */}
                    {canManage && (
                      <ExpandableSection
                        label={
                          participantCount > 0
                            ? `${t('Participants')} (${participantCount})`
                            : t('Participants')
                        }
                        expanded={participantsExpanded}
                        onToggle={() => toggleParticipants(row.scheduleId)}
                        testId={`cadence-participants-toggle-${row.cadenceType}`}
                      >
                        <ParticipantPicker
                          participants={row.defaultParticipants}
                          onChange={(participants) =>
                            handleParticipantsChange(row.scheduleId, participants)
                          }
                          actors={actors}
                          domains={domains}
                          disabled={isSaving}
                          searchValue={activeActorSearchKey === participantsSearchKey ? actorSearch : ''}
                          onSearchChange={(value) => {
                            setActiveActorSearchKey(participantsSearchKey);
                            setActorSearch(value);
                          }}
                          isSearching={activeActorSearchKey === participantsSearchKey && searchedActorsQuery.isFetching && !!actorSearch.trim()}
                          data-testid={`cadence-participants-${row.cadenceType}`}
                        />
                      </ExpandableSection>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Operating mode advisory */}
      <Alert variant="info" icon={<Info className="w-4 h-4" />}>
        {t('Cadences marked as "Recommended" are suggested for')}{' '}
        <strong className="capitalize">{t(operatingMode)}</strong>{' '}
        {t('mode. Deactivating them is allowed but not recommended.')}
      </Alert>

      {/* Save / Discard — hidden for non-managers (TB-4 AC-SSV-04) */}
      {canManage && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleDiscard}
            disabled={!isDirty || isSaving}
            data-testid="cadence-discard"
          >
            {t('Discard')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            data-testid="cadence-save"
          >
            {isSaving ? t('Saving…') : t('Save changes')}
          </Button>
        </div>
      )}
    </div>
  );
}
